import type { BgMove, BgView } from "@bing/engine-backgammon";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DifficultySelect } from "../components/DifficultySelect";
import { MatchEndCard, namedMember } from "../components/MatchEndCard";
import type { SeatIdentity } from "./seat-identity";
import { StartRow, StartScreenShell } from "../components/StartScreenShell";
import { BackgammonColorContext, BackgammonColorSelect } from "../screens/BackgammonTable";
import { BACKGAMMON_MATCH_LENGTHS, type BackgammonMatchLength } from "@bing/shared";

/** Persian digits for the match-length labels (the board has its own copy; this screen is standalone). */
const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
function faDigits(n: number): string {
  return String(n).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}
import {
  getBackgammonColor,
  setBackgammonColor,
  type BackgammonCheckerColor,
} from "../settings/prefs";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import { BackgammonTable } from "../screens/BackgammonTable";
import {
  getBackgammonMatchLength,
  setBackgammonMatchLength,
  getBackgammonDifficulty,
  setBackgammonDifficulty,
} from "../settings/prefs";
import type { BotDifficulty } from "@bing/shared";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { takeResumeIntent } from "./fresh-solo";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";

/** Single-winner end overlay — the designer's gold-disc card (win / mars / loss), ported from
 * docs/stitch/backgammon-ui-ux/backgammon_game_end_overlays. A win gets a glowing gold trophy disc +
 * gold-bordered card; a loss is a muted disc with the somber settle. Reused by the online container. */
export function BackgammonEndOverlay({
  view,
  result,
  self,
  opponent,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  view: BgView;
  result: GameResult;
  /** card-98 item 12: the two players' rows — the same identities the board's panels get. */
  self?: SeatIdentity | null;
  opponent?: SeatIdentity | null;
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const won = result.winners.includes(view.you);
  const mag = view.magnitude ?? 1;
  /*
   * ⚠️ THIS CARD ENDS THE MATCH, NOT A دست (2026-08-14). `view.magnitude` describes only the FINAL
   * round, so on a multi-دست match «سگ‌مارس بردی!» would be announcing the last hand as if it were
   * the whole match. The مارس/سگ‌مارس headline is therefore kept ONLY for a one-دست match, where
   * the round IS the match; otherwise the card states the match plainly and shows the final score.
   */
  const singleRound = (view.matchTo ?? 1) <= 1;
  // THREE tiers, not two: 1 single · 2 مارس · 3 سگ‌مارس. `mag >= 2` used to collapse the top two,
  // so a triple would have been announced as a plain مارس.
  const title = won
    ? singleRound && mag >= 3
      ? fa.backgammon.sagMarsWin
      : singleRound && mag === 2
        ? fa.backgammon.marsWin
        : fa.backgammon.youWin
    : singleRound && mag >= 3
      ? fa.backgammon.sagMarsLose
      : singleRound && mag === 2
        ? fa.backgammon.marsLose
        : fa.backgammon.youLose;
  // A multiplied result states its stakes plainly — the title alone doesn't say what it's worth.
  // On a real match the honest second line is the SCORE it finished at.
  const stakes = singleRound
    ? mag >= 3
      ? fa.backgammon.tripleStakes
      : mag === 2
        ? fa.backgammon.doubleStakes
        : null
    : fa.backgammon.matchFinal(
        faDigits(view.score?.[view.you] ?? 0),
        faDigits(view.score?.[view.you === 0 ? 1 : 0] ?? 0),
      );
  /*
   * WHY IT ENDED, WHEN THE BOARD DID NOT.
   *
   * A forfeit («خروج بدون بازگشت») or a claim ends the MATCH from the room, so `view.status` is
   * still «playing» and nothing on the board explains the result. The card said «یک بازی عالی
   * داشتی!» — a pleasantry, and the wrong one: nobody played a great game, the opponent walked out.
   * The `reason` has ridden the `gameover` frame all along (`GameResult.reason`); this card simply
   * never read it. Same idiom as `ChessGame`, which already does.
   */
  const roomReason =
    result.reason === "resign"
      ? won
        ? fa.backgammon.endResign
        : fa.backgammon.endResignLost
      : result.reason === "claim"
        ? won
          ? fa.backgammon.endClaim
          : fa.backgammon.endClaimLost
        : /* THE TABLE REMOVED A SEAT WHOSE TURNS KEPT LAPSING (2026-09-21). At a PUBLIC table for
             two that ends the match, and تخته نرد removes at THREE misses, so this is the
             commonest new card on the platform. NOT «resign»: nobody left. */
          result.reason === "idle"
          ? won
            ? fa.backgammon.endIdle
            : fa.backgammon.endIdleLost
          : null;
  const subtitle = roomReason ?? (won ? fa.backgammon.winSubtitle : fa.backgammon.loseSubtitle);
  // card-98 item 12: the shared template — its gold disc is THIS card's own, now every game's; each
  // PLAYER a side (as شلم draws a team), the winner first, with تخته‌نرد's own figure: the match score —
  // or, in a one-دست match, the points the دست was worth (۱ / ۲ مارس / ۳)
  const opp = view.you === 0 ? 1 : 0;
  const order = won ? [view.you, opp] : [opp, view.you];
  const figure = (seat: number): string =>
    singleRound
      ? faDigits(result.winners.includes(seat) ? mag : 0)
      : faDigits(view.score?.[seat] ?? 0);
  return (
    <MatchEndCard
      label={fa.backgammon.gameOver}
      outcome={won ? "won" : "lost"}
      glyph="🎲"
      title={title}
      badge={
        stakes ? (
          <div
            data-testid="bg-stakes"
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".04em",
              padding: "3px 12px",
              borderRadius: 999,
              color: "#1a0f0a",
              background: "linear-gradient(180deg,#ffe088,#d4af37)",
              boxShadow: "0 2px 6px rgba(0,0,0,.35)",
            }}
          >
            {stakes}
          </div>
        ) : null
      }
      reason={subtitle}
      sides={order.map((seat) => ({
        key: `seat-${seat}`,
        mine: seat === view.you,
        members: [
          seat === view.you
            ? namedMember(seat, self, fa.backgammon.you, "fox")
            : namedMember(seat, opponent, fa.backgammon.opponent, "wolf"),
        ],
        result: figure(seat),
        attrs: { "data-seat": seat },
      }))}
      cardClassName={won && singleRound && mag >= 2 ? "end-card--gammon" : undefined}
      playAgain={fa.backgammon.playAgain}
      leave={fa.backgammon.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** The pre-game START screen: pick the opponent level ONCE; it is LOCKED for the whole game (no
 * mid-game picker). «بازی دوباره» returns here, so the level is always a deliberate start-of-game
 * choice and never changes mid-game — mirrors the chess start screen. */
function BackgammonStartScreen({
  color,
  onColorChange,
  difficulty,
  matchLength,
  onMatchLengthChange,
  onChange,
  onStart,
  onLeave,
}: {
  difficulty: BotDifficulty;
  onChange: (level: BotDifficulty) => void;
  /** ITEM 12 — the checker colour the player takes. */
  color: BackgammonCheckerColor;
  onColorChange: (color: BackgammonCheckerColor) => void;
  /** Match target in POINTS (مارس ۲, سگ‌مارس ۳) — locked for the game once «شروع» is tapped. */
  matchLength: BackgammonMatchLength;
  onMatchLengthChange: (value: BackgammonMatchLength) => void;
  onStart: () => void;
  onLeave: () => void;
}): ReactNode {
  return (
    <StartScreenShell
      title={fa.backgammon.title}
      emoji="🎲"
      heading={fa.backgammon.chooseDifficulty}
      leaveLabel={fa.backgammon.leave}
      startLabel={fa.backgammon.startGame}
      onStart={onStart}
      onLeave={onLeave}
    >
      <DifficultySelect
        value={difficulty}
        onChange={onChange}
        ariaLabel={fa.backgammon.difficulty}
        compact
      />
      {/* ITEM 12 — pick your checkers BEFORE the game, alongside the difficulty. */}
      <StartRow label={fa.backgammon.color}>
        <BackgammonColorSelect value={color} onChange={onColorChange} />
      </StartRow>
      {/* MATCH LENGTH — points, not rounds (مارس ۲, سگ‌مارس ۳). Locked once the game starts,
          exactly like the difficulty: a saved game finishes at the length it began with. */}
      <StartRow label={fa.backgammon.matchLength}>
        {/* TWO ROWS OF TWO, in منچ's own segmented container — not a third layout. Four lengths
            («۱ / ۳ / ۵ / ۷ دست») on ONE row leave each button ~58px at 320, which is a label with
            nowhere to breathe; منچ's player-count picker is the treatment this borrows (bordered
            container, `flex-1` pills), simply wrapped because there are four options and not three. */}
        <div
          role="radiogroup"
          aria-label={fa.backgammon.matchLength}
          className="grid w-full grid-cols-2 gap-2"
        >
          {BACKGAMMON_MATCH_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={matchLength === n}
              onClick={() => onMatchLengthChange(n)}
              /* EACH OPTION ITS OWN BOX, so the four read as four choices rather than as one
                 segmented block. `border-solid` is not optional — Tailwind's preflight is off here,
                 so a bare `border` sets a width the browser then discards. */
              className={`m-0 min-w-0 cursor-pointer appearance-none rounded-xl border border-solid px-2 py-2 text-center font-body-md text-body-md font-bold transition-colors ${
                matchLength === n
                  ? "border-secondary bg-secondary text-ink-on-gold shadow-sm"
                  : "border-outline-variant bg-surface-container text-on-surface-variant hover:text-on-surface"
              }`}
            >
              {fa.backgammon.matchLengthOption(faDigits(n))}
            </button>
          ))}
        </div>
      </StartRow>
    </StartScreenShell>
  );
}

/** Owns the solo تخته نرد connection + store for ONE game at a FIXED (locked) difficulty. The SAME
 * generic paced solo path as chess (makeSoloRoom ↔ createGameStore). «بازی دوباره» → the start screen. */
function BackgammonSoloRound({
  difficulty,
  matchLength,
  onAgain,
  onExit,
}: {
  difficulty: BotDifficulty;
  matchLength: BackgammonMatchLength;
  onAgain: () => void;
  onExit: () => void;
}): ReactNode {
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  // Bumped by the channel gate's «عضو شدم» to re-attempt the join (a fresh store).
  const [round, setRound] = useState(0);
  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<BgMove, BgView>({
          wsUrl: GAME_SERVER_URL,
          room: "backgammon",
          getInitData: () => initData,
          difficulty, // FIXED for this game — chosen on the start screen, never changes mid-game
          matchLength, // …and so is the match target (points, not rounds)
        }),
      ),
    [initData, round, difficulty, matchLength],
  );
  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // The winning checker must be SEEN coming off before the end card covers the board, so the reveal
  // waits out the replay's drain (a doubles turn can still have three walks queued at match point).
  const [replayDraining, setReplayDraining] = useState(false);
  const showEnd = useEndReveal(result !== null, replayDraining ? 2600 : undefined);
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      onExit();
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  const { loading, progress } = useSoloLoad(view != null, ready, store);

  const doLeave = (): void => {
    store.getState().disconnect();
    onExit();
  };
  // «بازی دوباره» → back to the START screen so the next game re-chooses the (locked) level.
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.backgammon.connectionError}
          </p>
          <Button variant="primary" onClick={onExit}>
            {fa.backgammon.leave}
          </Button>
        </div>
      </div>
    );
  }
  // The connecting splash carries its OWN exit: without it the player was trapped from «شروع بازی»
  // until the table finished loading (the operator's "you have to fully enter the game and only
  // then tap exit"). Leaving here tears the connection down, exactly like leaving the table.
  if (loading || !view)
    return (
      <Boot
        caption={fa.backgammon.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.backgammon.leave}
      />
    );

  return (
    <>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <BackgammonTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : () => setConfirmingLeave(true)}
        self={profile}
        onDraining={setReplayDraining}
      />
      {result && showEnd ? (
        <BackgammonEndOverlay
          result={result}
          view={view}
          self={profile}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            clearActiveGame("backgammon");
            // swallow a rejected endGame() (a bare `.finally` re-throws it → unhandled rejection);
            // leaving proceeds regardless (the server-side end is best-effort, like the card games).
            void store
              .getState()
              .endGame()
              .catch(() => {})
              .finally(doLeave);
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
    </>
  );
}

/** Solo تخته نرد: the START screen (choose the LOCKED opponent level) → the game. «بازی دوباره»
 * returns to the start screen, so the level is ALWAYS a deliberate start-of-game choice. */
export function BackgammonGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  // ITEM 3 — an explicit «ادامه» from the lobby skips this picker. Showing it would ask for
  // settings the server is about to ignore in favour of the saved game — the same silent
  // override, one screen later. A fresh start still lands here.
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getBackgammonDifficulty());
  const [matchLength, setMatchLength] = useState<BackgammonMatchLength>(getBackgammonMatchLength());
  // ITEM 12 — the checker colour, chosen before the game and remembered for next time.
  const [color, setColor] = useState<BackgammonCheckerColor>(getBackgammonColor());

  if (phase === "select") {
    return (
      <BackgammonStartScreen
        color={color}
        onColorChange={(next) => {
          setColor(next);
          setBackgammonColor(next);
        }}
        difficulty={difficulty}
        onChange={(level) => {
          setDifficulty(level);
          setBackgammonDifficulty(level); // remember for next time (the picker pre-selects it)
        }}
        matchLength={matchLength}
        onMatchLengthChange={(value) => {
          setMatchLength(value);
          setBackgammonMatchLength(value); // remembered, like the difficulty and the colour
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  // ITEM 12 — the picked colour reaches every chip through the context, so nothing between the
  // board and the individual checker needs to know about it.
  return (
    <BackgammonColorContext.Provider value={color}>
      <BackgammonSoloRound
        difficulty={difficulty}
        matchLength={matchLength}
        onAgain={() => setPhase("select")}
        onExit={() => setView("lobby")}
      />
    </BackgammonColorContext.Provider>
  );
}
